import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/utils/dateUtils";
import { whatsappApi } from "@/services/whatsapp-api";

interface WhatsAppTranscriptDialogProps {
  conversationId: string | null;
  /** Mensagens que embasaram a conclusão do item (destacadas + scroll até elas). */
  highlightIds?: string[];
  onClose: () => void;
}

const AUTOR_STYLES: Record<string, string> = {
  CONTATO: "bg-muted",
  ATENDENTE: "bg-blue-50",
  IA: "bg-indigo-50",
  "NOTA INTERNA": "bg-amber-50 border border-dashed border-amber-300",
};

export function WhatsAppTranscriptDialog({
  conversationId,
  highlightIds = [],
  onClose,
}: WhatsAppTranscriptDialogProps) {
  const firstHighlightRef = useRef<HTMLDivElement | null>(null);

  const { data: transcript, isLoading } = useQuery({
    queryKey: ["whatsapp-transcript", conversationId],
    queryFn: () => whatsappApi.getTranscript(conversationId!),
    enabled: !!conversationId,
  });

  const highlights = new Set(highlightIds);
  const firstHighlightId = transcript?.messages.find((m) => highlights.has(m.id))?.id;

  useEffect(() => {
    if (transcript && firstHighlightRef.current) {
      firstHighlightRef.current.scrollIntoView({ block: "center" });
    }
  }, [transcript]);

  return (
    <Dialog open={!!conversationId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Conversa com {transcript?.contact_name ?? "contato"}
          </DialogTitle>
          <DialogDescription>
            {transcript?.telefone ?? ""}
            {highlights.size > 0 &&
              " · Mensagens destacadas embasaram a conclusão do item."}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
            {(transcript?.messages ?? []).map((message) => {
              const highlighted = highlights.has(message.id);
              return (
                <div
                  key={message.id}
                  ref={message.id === firstHighlightId ? firstHighlightRef : undefined}
                  className={cn(
                    "rounded-md p-2 text-sm",
                    AUTOR_STYLES[message.autor] ?? "bg-muted",
                    message.autor === "CONTATO" ? "mr-10" : "ml-10",
                    highlighted && "ring-2 ring-primary",
                  )}
                >
                  <div className="mb-0.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="font-medium">{message.autor}</span>
                    <span>{formatDateTime(message.created_at)}</span>
                  </div>
                  <div className="whitespace-pre-wrap">{message.texto}</div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
